import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { ShoppingCart, Menu, X, ChevronDown } from "lucide-react";
import { useCart } from "../context/CartContext";
import SafeImage from "./SafeImage";
import { useInstallPromptEvent, triggerInstall } from "./PWAInstallPrompt";
import { canOfferInstall, InstallGuideModal } from "./InstallWebApp";
import { useCategories } from "../lib/categories";

// Top-level desktop nav. Items with a `children` array render as a
// dropdown (Legal → Privacy / Terms). Mobile drawer flattens children
// into inline links so users don't need a second tap.
const NAV = [
  { to: "/shop", label: "Shop", testid: "nav-shop" },  {
    label: "Tools",
    testid: "nav-tools",
    children: [
      {
        to: "/tools/retatrutide-calculator",
        label: "Dosage Calculator",
        testid: "nav-tools-calculator",
      },
    ],
  },
  { to: "/lab-results", label: "Lab results", testid: "nav-tests" },
  { to: "/reviews", label: "Reviews", testid: "nav-reviews" },
  { to: "/orders", label: "Track Order", testid: "nav-orders" },
  { to: "/contact", label: "Contact Us", testid: "nav-contact" },
  {
    label: "Articles",
    testid: "nav-research",
    children: [
      { to: "/news", label: "News", testid: "nav-news" },
      { to: "/about", label: "About", testid: "nav-about" },
      { to: "/faq", label: "FAQ", testid: "nav-faq" },
      {
        to: "/research/in-house",
        label: "In-house articles",
        testid: "nav-research-inhouse",
      },
      {
        to: "/research/other",
        label: "Other articles",
        testid: "nav-research-other",
      },
    ],
  },
  {
    label: "Legal",
    testid: "nav-legal",
    children: [
      { to: "/privacy", label: "Privacy Policy", testid: "nav-privacy" },
      { to: "/terms", label: "Terms of Use", testid: "nav-terms" },
    ],
  },
];

// Lightweight nav dropdown (replaces Radix DropdownMenu — the Radix +
// floating-ui + remove-scroll stack cost ~30 KB gz in the critical main
// bundle for three simple menus). Click to toggle, closes on outside
// pointer-down, Escape, or item click.
function NavDropdown({ item, pathname, linkBase, activeCls, installAction }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // A parent route is "active" when the user is on any of its
  // child paths, mirroring NavLink's isActive contract.
  const isActive = item.children.some((c) => pathname.startsWith(c.to));

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        data-testid={item.testid}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className={`${linkBase} ${isActive ? activeCls : ""} inline-flex items-center gap-1 outline-none`}
      >
        {item.label}
        <ChevronDown
          size={14}
          className={`opacity-70 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute left-1/2 -translate-x-1/2 top-full mt-3 z-50 min-w-[12rem] rounded-md bg-[#0a0a0a] border border-white/15 text-white py-1 shadow-xl"
        >
          {item.children.map((c) => (
            <NavLink
              key={c.to}
              to={c.to}
              role="menuitem"
              data-testid={c.testid}
              onClick={() => setOpen(false)}
              className={({ isActive: ia }) =>
                `block w-full px-3 py-2 text-sm font-semibold uppercase tracking-[0.12em] cursor-pointer hover:bg-white/10 focus:bg-white/10 outline-none ${ia ? "text-white" : "text-white/80 hover:text-white"}`
              }
            >
              {c.label}
            </NavLink>
          ))}
          {installAction && (
            <button
              type="button"
              role="menuitem"
              data-testid="nav-tools-install"
              onClick={() => {
                setOpen(false);
                installAction();
              }}
              className="block w-full text-left px-3 py-2 text-sm font-semibold uppercase tracking-[0.12em] cursor-pointer text-white/80 hover:text-white hover:bg-white/10 focus:bg-white/10 outline-none"
            >
              Install Web App
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default function Header() {
  const { count, setOpen } = useCart();
  const { pathname } = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  // SHOP is a dropdown fed by the live product groups so new groups the
  // admin creates appear automatically.
  const categories = useCategories();
  const navItems = [
    {
      label: "Shop",
      testid: "nav-shop",
      children: [
        { to: "/shop", label: "All Products", testid: "nav-shop-all" },
        ...categories.map((c) => ({
          to: `/shop/category/${c.slug}`,
          label: c.name,
          testid: `nav-shop-${c.slug}`,
        })),
      ],
    },
    ...NAV.slice(1),
  ];
  // "Install Web App" availability — computed after mount so the
  // static prerender (no window) matches the first client render.
  const [offerInstall, setOfferInstall] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const { deferredPromptEvent } = useInstallPromptEvent();
  useEffect(() => {
    setOfferInstall(canOfferInstall());
  }, []);
  const handleInstallClick = async () => {
    // One-tap native install when the browser offers it (Chrome/Edge);
    // otherwise show the per-browser instruction popup.
    if (deferredPromptEvent) {
      const outcome = await triggerInstall();
      if (outcome === "accepted") {
        localStorage.setItem("pepura_pwa_installed", "1");
        setOfferInstall(false);
      }
      if (outcome) return;
    }
    setGuideOpen(true);
  };
  // Cart-badge bounce: bump a key whenever `count` GROWS (never
  // shrinks — removing an item shouldn't be celebrated). Re-keying
  // the badge node makes React unmount/remount it, which restarts
  // the CSS animation cleanly without us tracking elapsed time or
  // fighting with `animationend` listeners.
  const prevCount = useRef(count);
  const [bounceKey, setBounceKey] = useState(0);
  useEffect(() => {
    if (count > prevCount.current) {
      setBounceKey((k) => k + 1);
    }
    prevCount.current = count;
  }, [count]);
  const linkBase =
    "text-sm font-semibold uppercase tracking-[0.15em] text-white/70 hover:text-white transition-colors";
  const active = "text-white";

  return (
    <>
    <header
      data-testid="site-header"
      className="sticky top-0 z-40 backdrop-blur-xl bg-[#0a0a0a]/85 border-b border-white/50"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 py-4 flex items-center justify-between gap-3">
        {/* Hamburger (mobile only) */}
        <button
          type="button"
          data-testid="open-menu-btn"
          onClick={() => setMenuOpen(true)}
          aria-label="Open menu"
          className="md:hidden inline-flex items-center justify-center h-10 w-10 rounded-full border border-white/15 bg-white/5 text-white hover:bg-white/10"
        >
          <Menu size={20} />
        </button>

        <Link
          to="/"
          data-testid="brand-link"
          className="flex items-center gap-2 sm:gap-3 group"
        >
          <SafeImage
            src="/Pepura-icon-header.avif"
            alt="Pepura Labs logo"
            data-testid="brand-logo"
            className="h-9 sm:h-11 w-auto transition-transform group-hover:scale-105"
          />
          <span
            data-testid="brand-wordmark"
            className="font-display text-2xl sm:text-3xl tracking-tight font-[700] text-white"
          >
            Pepura Labs
          </span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-10">
          {navItems.map((n) => {
            if (n.children) {
              return (
                <NavDropdown
                  key={n.label}
                  item={n}
                  pathname={pathname}
                  linkBase={linkBase}
                  activeCls={active}
                  installAction={
                    n.label === "Tools" && offerInstall
                      ? handleInstallClick
                      : null
                  }
                />
              );
            }
            return (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={n.testid}
                className={({ isActive }) =>
                  `${linkBase} ${isActive ? active : ""}`
                }
              >
                {n.label}
              </NavLink>
            );
          })}
        </nav>

        {/* Cart */}
        <button
          type="button"
          data-testid="open-cart-btn"
          onClick={() => setOpen(true)}
          aria-label="Open cart"
          data-cart-fly-target
          className="relative inline-flex items-center justify-center md:gap-2 h-10 md:h-auto w-10 md:w-auto md:px-4 md:py-2 rounded-full border border-white/15 bg-white/5 text-white hover:bg-white/10"
        >
          <ShoppingCart size={16} />
          <span className="hidden md:inline text-xs font-semibold uppercase tracking-[0.15em]">
            Cart
          </span>
          {count > 0 && (
            <span
              key={bounceKey}
              data-testid="cart-count-badge"
              className="absolute md:static -top-1 -right-1 md:ml-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-[#20a6da] px-1.5 text-[11px] font-bold text-[#0a0a0a] animate-cart-bounce"
            >
              {count}
            </span>
          )}
        </button>
      </div>

      {/* Mobile drawer is rendered outside <header> so its
          position:fixed is relative to the viewport, not the header
          (the header creates a containing block via backdrop-filter). */}
    </header>
    {menuOpen && (
      <div
        data-testid="mobile-menu"
        className="md:hidden fixed inset-0 z-[60] bg-[#0a0a0a] animate-slide-up flex flex-col"
        style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}
      >
        <div className="flex items-center justify-between px-4 py-4 border-b border-white/10 shrink-0">
          <span className="font-display text-lg uppercase tracking-tight font-[700] text-white">
            Pepura Labs Menu
          </span>
          <button
            type="button"
            data-testid="close-menu-btn"
            onClick={() => setMenuOpen(false)}
            aria-label="Close menu"
            className="inline-flex items-center justify-center h-10 w-10 rounded-full border border-white/15 bg-white/5 text-white"
          >
            <X size={18} />
          </button>
        </div>
        <nav
          className="flex flex-col p-4 pb-10 gap-1 flex-1 overflow-y-auto overscroll-contain"
          style={{ WebkitOverflowScrolling: "touch" }}
        >
          {navItems.flatMap((n) => {
            if (n.children) {
              // Flatten children into inline links on mobile (preceded by
              // a small section label) so it's a single tap rather than a
              // nested drawer.
              return [
                <p
                  key={`${n.label}-label`}
                  className="mt-4 px-1 text-[11px] uppercase tracking-[0.18em] text-white/50"
                >
                  {n.label}
                </p>,
                ...n.children.map((c) => (
                  <NavLink
                    key={c.to}
                    to={c.to}
                    end={c.to === "/shop"}
                    data-testid={`mobile-${c.testid}`}
                    onClick={() => setMenuOpen(false)}
                    className={({ isActive }) =>
                      `${
                        isActive ? "text-[#20a6da]" : "text-white"
                      } font-display text-2xl uppercase tracking-tight font-[700] py-3 border-b border-white/10`
                    }
                  >
                    {c.label}
                  </NavLink>
                )),
                ...(n.label === "Tools" && offerInstall
                  ? [
                      <button
                        key="mobile-install-web-app"
                        type="button"
                        data-testid="mobile-nav-tools-install"
                        onClick={() => {
                          setMenuOpen(false);
                          handleInstallClick();
                        }}
                        className="text-left text-white font-display text-2xl uppercase tracking-tight font-[700] py-3 border-b border-white/10"
                      >
                        Install Web App
                      </button>,
                    ]
                  : []),
              ];
            }
            const to = n.to;
            return [
              <NavLink
                key={n.to}
                to={to}
                end={to === "/"}
                data-testid={`mobile-${n.testid}`}
                onClick={() => setMenuOpen(false)}
                className={({ isActive }) =>
                  `${
                    isActive ? "text-[#20a6da]" : "text-white"
                  } font-display text-2xl uppercase tracking-tight font-[700] py-3 border-b border-white/10`
                }
              >
                {n.label}
              </NavLink>,
            ];
          })}
        </nav>
      </div>
    )}
    <InstallGuideModal open={guideOpen} onClose={() => setGuideOpen(false)} />
    </>
  );
}
